import { FC, useEffect, useMemo, useState } from 'react';

import { IoMenu } from 'react-icons/io5';
import { IoMdNotificationsOutline } from 'react-icons/io';

import { useRouter } from 'next/router';

import { parseCookies } from 'nookies';
import { useQuery } from '@tanstack/react-query';
import { Badge, IconButton } from '@mui/material';

import { isAdminProfile } from '~/common/utils/authorization';
import { getUnreadNotificationsCount } from '~/common/services/controllers/notifications';
import { useBusiness } from '~/common/providers/business';
import { useAuth } from '~/common/providers/auth';
import { profilesIds } from '~/common/constants/profiles-ids';
import { cookies } from '~/common/constants/cookies';
import { VmcoreLogo } from '~/common/components/VmcoreLogo';
import { MonteiroEngenhariaLogo } from '~/common/components/MonteiroEngenhariaLogo';

import { UserMenu } from './UserMenu';
import { StyActions, StyHeader, StyLogoWrapper } from './styles';

interface IHeader {
  openSidebarDrawer?: boolean;
  setOpenSidebarDrawer?: (open: boolean) => void;
  hideNotifications?: boolean;
  forceLogo?: boolean;
  /** Exibe o logo da plataforma (VMCORE) no lugar do logo whitelabel do negocio */
  platformLogo?: boolean;
  disableSidebarMenu?: boolean;
  hideUserProfileItem?: boolean;
  showInlineLogoutButton?: boolean;
  userOverride?: {
    name?: string;
    avatar?: string;
    profile?: {
      name?: string;
    };
  } | null;
  onLogoutOverride?: () => void;
}

const hasActiveAuthToken = (): boolean => {
  const { [cookies.AUTH_TOKEN]: authToken } = parseCookies();

  return Boolean(authToken);
};

export const Header: FC<IHeader> = ({
  openSidebarDrawer,
  setOpenSidebarDrawer,
  hideNotifications = false,
  forceLogo = false,
  platformLogo = false,
  disableSidebarMenu = false,
  hideUserProfileItem = false,
  showInlineLogoutButton = false,
  userOverride,
  onLogoutOverride,
}) => {
  const { user } = useAuth();
  const { business } = useBusiness();
  const activeUser = userOverride || user;
  const router = useRouter();
  const [render, setRender] = useState<boolean>(false);

  const notificationsEnabled =
    !hideNotifications && !userOverride && Boolean(user);

  const { data: unreadData } = useQuery({
    queryKey: ['unreadNotificationsCount'],
    queryFn: async () => {
      const { data } = await getUnreadNotificationsCount();
      return data;
    },
    enabled: render && notificationsEnabled && hasActiveAuthToken(),
    refetchOnWindowFocus: true,
    staleTime: 30000,
  });

  const unreadCount = unreadData?.count ?? 0;

  useEffect(() => {
    setRender(true);
  }, []);

  const shouldRenderSidebarAccessMenu = useMemo(() => {
    if (disableSidebarMenu || !activeUser || userOverride) {
      return false;
    }

    if (
      (activeUser as any).employeeId &&
      (activeUser as any).profile.id === profilesIds.EMPLOYEE
    ) {
      return false;
    }

    return true;
  }, [activeUser, disableSidebarMenu, userOverride]);

  const shouldRenderLogoAtHeader = useMemo(() => {
    if (forceLogo) {
      return true;
    }

    if (!activeUser || userOverride) {
      return false;
    }

    if (isAdminProfile((activeUser as any).profile)) {
      return false;
    }

    if (
      (activeUser as any).employeeId &&
      (activeUser as any).profile.id !== profilesIds.EMPLOYEE
    ) {
      return false;
    }

    return true;
  }, [activeUser, forceLogo, userOverride]);

  if (!render) {
    return null;
  }

  return (
    <StyHeader>
      {shouldRenderSidebarAccessMenu && (
        <IconButton onClick={() => setOpenSidebarDrawer?.(!openSidebarDrawer)}>
          <IoMenu size={24} color="#FFFFFF" />
        </IconButton>
      )}
      {shouldRenderLogoAtHeader && (
        <StyLogoWrapper style={{ display: 'flex' }}>
          {platformLogo || business?.slug === 'vmcore' ? (
            <VmcoreLogo variant="horizontal" tone="light" height={32} />
          ) : (
            <MonteiroEngenhariaLogo width={180} height={44} />
          )}
        </StyLogoWrapper>
      )}

      <StyActions>
        {!hideNotifications && (
          <IconButton onClick={() => router.push('/notificacoes')}>
            <Badge color="primary" max={99} badgeContent={unreadCount}>
              <IoMdNotificationsOutline size={24} color="#FFFFFF" />
            </Badge>
          </IconButton>
        )}
        <UserMenu
          displayUser={userOverride}
          onLogout={onLogoutOverride}
          hideProfileItem={hideUserProfileItem}
          showInlineLogoutButton={showInlineLogoutButton}
          inverted
        />
      </StyActions>
    </StyHeader>
  );
};
